import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Database, CheckCircle, XCircle, Loader2, Info, Upload } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DataMigration = () => {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers = { 
        Authorization: `Bearer ${token}`
      };

      const response = await axios.post(
        `${API}/import-data/upload`,
        formData,
        { headers }
      );

      setResults(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Database className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Import QC Incidents</h1>
        </div>
        <p className="text-gray-600">
          Import QC incidents data from JSON file
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
        {!results ? (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-2">How to Import QC Incidents:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Upload your QC incidents JSON file</li>
                    <li>Click "Import QC Incidents"</li>
                    <li>Existing QC incidents will be replaced with the imported data</li>
                  </ol>
                  <p className="mt-3 text-amber-700 font-medium">
                    ⚠️ Warning: This will replace all existing QC incident records
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Export File
              </label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {file && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex gap-2">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-900">{error}</p>
                </div>
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Importing QC Incidents...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Import QC Incidents
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                QC Incidents Import Complete!
              </h3>
              <p className="text-gray-600">
                {results.imported_count || 0} QC incidents imported successfully
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Import Results</h3>
              </div>
              <div className="p-6 space-y-2 max-h-96 overflow-y-auto">
                {results.results.map((result) => (
                  <div
                    key={result.collection}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      result.status === 'success'
                        ? 'bg-green-50 border-green-200'
                        : result.status === 'error'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{result.collection}</p>
                      {result.status === 'success' && (
                        <p className="text-xs text-gray-600">
                          Imported {result.imported} documents
                        </p>
                      )}
                      {result.message && (
                        <p className="text-xs text-gray-600">{result.message}</p>
                      )}
                    </div>
                    {result.status === 'success' && <CheckCircle className="w-5 h-5 text-green-600" />}
                    {result.status === 'error' && <XCircle className="w-5 h-5 text-red-600" />}
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => {
                setFile(null);
                setResults(null);
                setError(null);
              }}
              className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Import Another File
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DataMigration;
